<template>
  <div>
		<div class="ibox">
			<a class="ibox-results-collapse visible-xs" role="button" data-toggle="collapse" href=".ibox-results-filters-collapse-box" aria-expanded="false" aria-controls="ibox-results-filters-collapse-box">
				Show Filters <i class="fa fa-bars"></i>
			</a>
			<div class="ibox-results-filters-collapse-box collapse in">
				<div class="ibox-results-filters">
					<div class="row">
						<div class="col-md-3" v-if="$user.isAdminOrCoordinator">
							<div class="form-group">
								<label class="control-label">Select User</label>
                <selectize
                  v-model="user_id"
                  :options="initialUserOptions"
                  :fetch-results="fetchUserResults"
                  name="user_id">
                </selectize>
							</div>
						</div>
						<div class="col-md-3">
							<div class="form-group">
								<label class="control-label">Keyword</label>
								<input type="text" class="form-control" placeholder="Location Name or Address" @input="updateKeywords"/>
							</div>
						</div>
						<div class="col-md-3">
							<div class="form-group">
								<label class="control-label">Contact Name/Phone</label>
								<input type="text" class="form-control" placeholder="..." @input="updateContactName"/>
							</div>
						</div>
						<div class="col-md-3">
							<div class="form-group">
								<label class="control-label">State</label>
                <selectize
                  v-model="state"
                  name="state"
                  :options="stateOptions"
                  class="form-control select2-material-placeholder">
                </selectize>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="ibox-content">
				<ul class="list-detail list-detail-locations">
					<li v-for="location in locations">
						<div class="location-favourite" :class="{ 'favourite' : location.favourite }">
							<i class="fa fa-star" @click="handleFavouriteLocation(location.id)"></i>
						</div>
						<router-link :to="{ name: 'locations.edit', params: { id: location.id }}" @click.native="setActiveLocation(location)">
							<h2 class="truncate"><strong>{{location.name}} </strong>
                <template v-if="location.suburb">
                  - {{location.suburb}}
                </template>
              </h2>
							<p class="truncate" v-if="location.address">
                {{location.address}}
              </p>
              <p class="truncate" v-else>
                &nbsp;
              </p>
						</router-link>
						<div class="map-thumb hidden-xs">
              <img src="~assets/img/map-thumb-512.png" @click="openLocationMapModal(location)">
						</div>
					</li>
				</ul>

        <div class="text-center text-muted" v-show="haveNoLocations">
          <strong>There are no locations.</strong>
        </div>
			</div>

      <lazy-loading v-show="haveLocations" :reached-bottom="getLocationsForNextPage" :filters="filters" ref="lazyLoading">
        <strong slot="finished" class="text-muted">There are no more locations.</strong>
      </lazy-loading>
		</div>

    <div class="modal fade modal-location-map" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document" v-if="showingLocationMapModal">
        <div class="modal-content">
          <div class="modal-header">
            {{ selectedMapLocation.name }}
          </div>
          <div class="modal-body">
            <gmap-map
              ref="map"
              v-if="selectedMapLocation.latitude && selectedMapLocation.longitude"
              :center="{ lat: selectedMapLocation.latitude, lng: selectedMapLocation.longitude }"
              style="width: 100%; height:600px"
              :zoom="8">
              <gmap-marker
                :position="{ lat: selectedMapLocation.latitude, lng: selectedMapLocation.longitude }">
              </gmap-marker>
            </gmap-map>
            <p class="m-t-lg lead text-center" v-else>
              There is no map location set.
            </p>
          </div>
          <div class="modal-footer">
            <div class="row">
              <div class="col-sm-6 footer-left">
                <button type="button" class="btn btn-sm btn-default" data-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import { mapActions, mapGetters, mapMutations } from 'vuex'
  import { debounce, isEqual, cloneDeep } from 'lodash'
  import { notify } from 'utils'
  import { LazyLoading } from 'components/misc'
  import { Selectize } from 'components/forms'
  import { SET_LOCATION } from 'vx/mutations'
  import states from 'mixins/states'
  import $ from 'jquery'

  export default {
    mixins: [states],

    components: {
      Selectize,
      LazyLoading
    },

    data () {
      return {
        initialUserOptions: [],
        selectedMapLocation: null,
        showingLocationMapModal: false,
        user_id: null,
        keywords: '',
        contact_name: '',
        state: ''
      }
    },

    watch: {
      filters: {
        handler (newFilters, oldFilters) {
          if (isEqual(newFilters, oldFilters)) {
            return
          }

          // Reset the lazy loading component so that it will attempt to load
          // more jobs as we scroll again.
          const resetLazyLoading = () => (this.$refs.lazyLoading.$emit('reset'))

          this.getLocations(this.filters).then(resetLazyLoading).catch(this.failedToLoadLocations)
        },
        deep: true
      }
    },

    computed: {
      ...mapGetters('location', { locations: 'locationsDecorated' }),
      ...mapGetters('user', ['userOptionsWithPublic']),

      /**
       * Filters as a computed object so we can watch for changes.
       *
       * @return object
       */
      filters () {
        return {
          contact_name: this.contact_name,
          keywords: this.keywords,
          state: this.state,
          user_id: this.user_id
        }
      },

      /**
       * Determine if we have no locations.
       *
       * @return boolean
       */
      haveNoLocations () {
        return this.locations.length === 0
      },

      /**
       * Determine if we have locations.
       *
       * @return boolean
       */
      haveLocations () {
        return this.locations.length > 0
      }
    },

    created () {
      if (this.$user.isAdminOrCoordinator) {
        const setInitialUserOptions = () => (this.initialUserOptions = cloneDeep(this.userOptionsWithPublic))

        this.getUsers({ master: true }).then(setInitialUserOptions).catch(this.failedToLoadUsers)
      }

      if (this.$route.query.user) {
        this.user_id = this.$route.query.user
      } else {
        this.getLocations().catch(this.failedToLoadLocations)
      }
    },

    mounted () {
      $('.modal-location-map').on('hidden.bs.modal', this.resetSelectedMapLocation)
      $('.modal-location-map').on('shown.bs.modal', this.resetSelectedMapZoom)
    },

    beforeDestroy () {
      $('.modal-location-map').off('hidden.bs.modal', this.resetSelectedMapLocation)
      $('.modal-location-map').off('shown.bs.modal', this.resetSelectedMapZoom)
    },

    methods: {
      ...mapActions('location', ['getLocations', 'getLocationsForNextPage', 'favouriteLocation']),
      ...mapMutations('location', [SET_LOCATION]),
      ...mapActions('user', ['getUsers']),

      /**
       * Notify when failing to load users.
       *
       * @return void
       */
      failedToLoadUsers () {
        notify.error('There was a problem loading the users.')
      },

      /**
       * Notify when failing to load locations.
       *
       * @return void
       */
      failedToLoadLocations () {
        notify.error('There was a problem loading the locations.')
      },

      /**
       * When admin types in the user select box we need to fetch more users
       * from the api since the users are paginated.
       */
      fetchUserResults (name, callback) {
        return this.getUsers({ master: true, name }).then(response => {
          callback(this.userOptionsWithPublic)
        }).catch(this.failedToLoadUsers)
      },

      /**
       * Set the active location as soon as we click through on a link to have the
       * feeling of loading the location instantly.
       *
       * @param void
       */
      setActiveLocation (location) {
        this[SET_LOCATION](location)
      },

      /**
       * Reset the selected map location on modal close.
       *
       * @return void
       */
      resetSelectedMapLocation () {
        this.showingLocationMapModal = false
        this.selectedMapLocation = null
      },

      /**
       * Open the location map modal for a given location.
       *
       * @param object location
       *
       * @return void
       */
      openLocationMapModal (location) {
        this.showingLocationMapModal = true

        this.selectedMapLocation = location

        this.$nextTick(() => ($('.modal-location-map').modal('show')))
      },

      /**
       * Reset the selected map zoom once modal has loaded.
       *
       * @return void
       */
      resetSelectedMapZoom () {
        this.$nextTick(() => {
          if (this.$refs.map) {
            this.$refs.map.resizePreserveCenter()
          }
        })
      },

      /**
       * Favourite a location.
       *
       * @param integer id
       *
       * @return void
       */
      handleFavouriteLocation (id) {
        return this.favouriteLocation(id)
      },

      /**
       * Debounce updating of keywords so we don't poll API immediately for new locations.
       *
       * @param object event
       *
       * @return Function
       */
      updateKeywords: debounce(function (event) {
        this.keywords = event.target.value
      }, 800),

      /**
       * Debounce updating of order reference so we don't poll API immediately for new locations.
       *
       * @param object event
       *
       * @return Function
       */
      updateContactName: debounce(function (event) {
        this.contact_name = event.target.value
      }, 800)
    }
  }
</script>



// WEBPACK FOOTER //
// Locations.vue?6d7765b9